import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Package, ArrowLeft, Plus, Download } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/toastContext';

/** 提取后端错误文案（detail 优先，其次 Error.message） */
const errorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

interface Essence {
  id: string;
  version: string;
  status: string;
  target_framework: string;
  knowledge?: { items: unknown[] };
  persona?: { samples: unknown[] };
  workflow?: { workflows: unknown[] };
  tools_pack?: { tools: unknown[]; mcp_servers: unknown[] };
  memory?: { memories: unknown[] };
  created_at: string;
}

export default function EssenceView() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [essences, setEssences] = useState<Essence[]>([]);
  const [loading, setLoading] = useState(true);

  const loadEssences = useCallback(async () => {
    try {
      // agents = projects：Agent 的 essence 列表走 project 维度（/projects/{id}/essences）
      const res = await apiClient.get(`/projects/${agentId}/essences`);
      setEssences(res.data);
    } catch (e) {
      console.error('Failed to load essences:', e);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadEssences();
    };
    tick();
  }, [loadEssences]);

  const createEssence = async () => {
    try {
      const res = await apiClient.post(`/projects/${agentId}/essences`, {
        version: '1.0.0',
        target_framework: 'generic'
      });
      setEssences([res.data, ...essences]);
    } catch (e) {
      console.error('Failed to create essence:', e);
      toast.error('生成 Essence 失败: ' + errorMessage(e));
    }
  };

  const publish = async (id: string) => {
    try {
      await apiClient.post(`/essences/${id}/publish`);
      setEssences(essences.map(e => e.id === id ? { ...e, status: 'published' } : e));
    } catch (e) {
      console.error('Failed to publish essence:', e);
      toast.error('发布失败: ' + errorMessage(e));
    }
  };

  const exportEssence = async (id: string, framework: string) => {
    try {
      const res = await apiClient.get(`/essences/${id}/export/${framework}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `essence-${framework}.json`;
      a.click();
    } catch (e) {
      console.error('Failed to export essence:', e);
      toast.error('导出失败: ' + errorMessage(e));
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <button className="secondary mb-4" onClick={() => navigate(`/agents/${agentId}`)}>
        <ArrowLeft size={16} /> 返回概览
      </button>

      <div className="card mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2><Package size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />Essence 导出</h2>
            <p>将 Agent 的能力打包为可移植的能力包</p>
          </div>
          <button className="primary" onClick={createEssence}>
            <Plus size={14} /> 生成 Essence
          </button>
        </div>
      </div>

      {essences.length === 0 ? (
        <div className="card">
          <div className="empty">
            <div className="empty-icon"><Package size={48} /></div>
            <h3>暂无 Essence</h3>
            <p>通过示教教会 Agent 后，可以生成能力包</p>
          </div>
        </div>
      ) : (
        <div className="list">
          {essences.map(essence => (
            <div key={essence.id} className="card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 style={{ margin: 0 }}>v{essence.version}</h3>
                  <span className={`badge ${essence.status === 'published' ? 'success' : 'warning'}`}>
                    {essence.status === 'published' ? '已发布' : '草稿'}
                  </span>
                </div>
                <span className="text-muted text-sm">
                  {new Date(essence.created_at).toLocaleDateString()}
                </span>
              </div>

              <p>目标框架: {essence.target_framework || '通用'}</p>
              <p>知识条目: {essence.knowledge?.items?.length || 0} 项</p>
              <p>人设样本: {essence.persona?.samples?.length || 0} 条</p>
              <p>工作流: {essence.workflow?.workflows?.length || 0} 个</p>
              <p>
                工具: {essence.tools_pack?.tools?.length || 0} 个 / MCP 服务:{' '}
                {essence.tools_pack?.mcp_servers?.length || 0} 个
              </p>
              <p>记忆策略: {essence.memory?.memories?.length || 0} 条</p>

              <div className="flex gap-2 mt-4">
                {essence.status !== 'published' && (
                  <button className="primary" onClick={() => publish(essence.id)}>
                    发布
                  </button>
                )}
                <button className="secondary" onClick={() => exportEssence(essence.id, 'celestial_toad')}>
                  <Download size={14} /> 导出 CT
                </button>
                <button className="secondary" onClick={() => exportEssence(essence.id, 'hermes')}>
                  <Download size={14} /> 导出 Hermes
                </button>
                <button className="secondary" onClick={() => exportEssence(essence.id, 'generic')}>
                  <Download size={14} /> 导出通用
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}